/**
 * MyJobDetail — v49.5. Public offer page with apply form (CV + letter PDF).
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, MapPin, Building2, DollarSign, Send, Loader2, Upload, FileText, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { getMediaUrl } from '../utils/mediaUrl';
import { JOB_L, money, contractLabel } from '../components/MyJob/i18n';
import { portfolioTexts } from '../components/portfolio/portfolioTexts';
import Navigation from '../components/Navigation';

const MyJobDetail = () => {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const { language } = useLanguage();
  const l = JOB_L[language] || JOB_L.fr;
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState({ applicant_name: '', applicant_email: '', message: '', cv_url: '', letter_url: '' });
  const [usePortfolio, setUsePortfolio] = useState(false);
  const PL = portfolioTexts(language);
  const [uploading, setUploading] = useState({ cv: false, letter: false });
  const [done, setDone] = useState(false);
  const cvRef = useRef(null);
  const letterRef = useRef(null);
  const keyboardHeight = useKeyboardHeight();

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const r = await axios.get(`/api/myjob/offers/${offerId}`); if (alive) setOffer(r.data); }
      catch (_) { /* silent */ } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [offerId]);

  const upload = async (which, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error(l.pdfOnly); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error(l.tooLarge); return; }
    setUploading(u => ({ ...u, [which]: true }));
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`/api/myjob/offers/${offerId}/uploads/${which}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, [`${which}_url`]: r.data.url }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload error'); }
    finally { setUploading(u => ({ ...u, [which]: false })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.applicant_name.trim() || !form.applicant_email.trim() || (!usePortfolio && (!form.cv_url || !form.letter_url))) {
      toast.error('Champs requis');
      return;
    }
    setApplying(true);
    try {
      await axios.post(`/api/myjob/offers/${offerId}/applications`, { ...form, cv_url: form.cv_url || undefined, letter_url: form.letter_url || undefined, use_portfolio: usePortfolio });
      toast.success(l.sent);
      setDone(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  if (!offer) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Offer not found</div>;

  return (
    <div
      className="min-h-screen bg-black text-white pb-24"
      data-testid="myjob-detail-page"
      style={{
        paddingBottom: `calc(6rem + ${keyboardHeight}px)`,
        transition: 'padding-bottom 180ms ease-out',
      }}
    >
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/films?tab=studio')} className="p-2 rounded-full hover:bg-white/10" data-testid="myjob-detail-back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-lg font-bold truncate">{offer.title}</h1>
      </header>

      <div className="relative h-56 bg-gradient-to-br from-amber-700/30 to-orange-800/30">
        {offer.cover_url ? <img src={getMediaUrl(offer.cover_url)} alt={offer.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Briefcase className="w-16 h-16 text-white/30" /></div>}
      </div>

      <main className="px-4 py-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-semibold">{contractLabel(offer.contract_type, language)}</span>
          {offer.company_name && <span className="flex items-center gap-1 text-sm text-white/80"><Building2 className="w-3.5 h-3.5" /> {offer.company_name}</span>}
          {offer.location && <span className="flex items-center gap-1 text-sm text-white/80"><MapPin className="w-3.5 h-3.5" /> {offer.location}</span>}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          {offer.position && <div><p className="text-white/50 text-xs">{l.position}</p><p className="font-semibold">{offer.position}</p></div>}
          {offer.category && <div><p className="text-white/50 text-xs">{l.category}</p><p className="font-semibold">{offer.category}</p></div>}
          {offer.salary_amount > 0 && <div><p className="text-white/50 text-xs">{l.salary}</p><p className="font-semibold flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {money(offer.salary_amount, offer.salary_currency)}</p></div>}
          <div><p className="text-white/50 text-xs">{l.by}</p><p className="font-semibold">{offer.pro_display_name || offer.pro_username}</p></div>
        </div>

        <div>
          <h3 className="text-sm font-bold mb-1">{l.desc}</h3>
          <p className="text-sm text-white/80 whitespace-pre-wrap">{offer.description}</p>
        </div>

        {done ? (
          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm flex items-center gap-2" data-testid="myjob-apply-success"><Send className="w-4 h-4" /> {l.sent}</div>
        ) : (
          <form onSubmit={submit} className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 space-y-2" data-testid="myjob-apply-form">
            <h3 className="text-sm font-bold flex items-center gap-2"><Send className="w-4 h-4 text-amber-400" /> {l.applyTo} « {offer.title} »</h3>
            <input required value={form.applicant_name} onChange={e => setForm({ ...form, applicant_name: e.target.value })} placeholder={l.name} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="myjob-name" />
            <input required type="email" value={form.applicant_email} onChange={e => setForm({ ...form, applicant_email: e.target.value })} placeholder={l.email} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="myjob-email" />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => cvRef.current?.click()} disabled={uploading.cv} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center justify-center gap-1 disabled:opacity-50" data-testid="myjob-cv-btn">
                {uploading.cv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : form.cv_url ? <FileText className="w-3.5 h-3.5 text-emerald-300" /> : <Upload className="w-3.5 h-3.5" />}
                {form.cv_url ? '✓ CV' : l.cv}
              </button>
              <input ref={cvRef} type="file" accept="application/pdf" onChange={e => upload('cv', e.target.files?.[0])} className="hidden" />
              <button type="button" onClick={() => letterRef.current?.click()} disabled={uploading.letter} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center justify-center gap-1 disabled:opacity-50" data-testid="myjob-letter-btn">
                {uploading.letter ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : form.letter_url ? <FileText className="w-3.5 h-3.5 text-emerald-300" /> : <Upload className="w-3.5 h-3.5" />}
                {form.letter_url ? '✓ Letter' : l.letter}
              </button>
              <input ref={letterRef} type="file" accept="application/pdf" onChange={e => upload('letter', e.target.files?.[0])} className="hidden" />
            </div>
            <textarea rows={2} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={l.msg} className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm" data-testid="myjob-message" />
            <div className="grid grid-cols-2 gap-2" data-testid="myjob-apply-mode">
              <button type="button" onClick={() => setUsePortfolio(false)} className={`py-1.5 rounded-full text-[11px] font-semibold ${!usePortfolio ? 'bg-amber-500/30 border border-amber-400' : 'bg-white/5'}`} data-testid="myjob-mode-classic">{PL.applyClassic}</button>
              <button type="button" onClick={() => setUsePortfolio(true)} className={`py-1.5 rounded-full text-[11px] font-semibold ${usePortfolio ? 'bg-fuchsia-500/30 border border-fuchsia-400' : 'bg-white/5'}`} data-testid="myjob-mode-portfolio">💼 {PL.applyWith}</button>
            </div>
            <button type="submit" disabled={applying || (!usePortfolio && (!form.cv_url || !form.letter_url))} className="w-full py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-black font-semibold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="myjob-submit">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {applying ? l.sending : l.send}
            </button>
          </form>
        )}
      </main>
      <Navigation />
    </div>
  );
};

export default MyJobDetail;
